import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiGithub, FiExternalLink } from 'react-icons/fi';

const ProjectsSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0.8rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #00ff00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: #00ff00;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 2.5rem;
  }
`;

const FilterContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.8rem;
    margin-bottom: 2.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.6rem;
    margin-bottom: 2rem;
  }
`;

const FilterButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: ${props => props.active ? 'rgba(0, 255, 0, 0.2)' : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${props => props.active ? 'rgba(0, 255, 0, 0.4)' : 'rgba(0, 255, 0, 0.2)'};
  border-radius: 25px;
  color: ${props => props.active ? '#00ff00' : '#ffffff'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  &:hover {
    background: rgba(0, 255, 0, 0.15);
    border-color: rgba(0, 255, 0, 0.3);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(0, 255, 0, 0.3);
  border-radius: 24px;
  padding: 2.5rem;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 2rem;
    min-height: 350px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    min-height: 300px;
    border-radius: 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00ff00, #3b82f6);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
    border-color: rgba(0, 255, 0, 0.5);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 255, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: 100%;
    display: block;
    transition: transform 0.3s ease;
    object-fit: cover;
  }
  
  &:hover img {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    height: 250px;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    height: 200px;
    margin-bottom: 1rem;
    border-radius: 12px;
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    gap: 0.8rem;
    margin-bottom: 1.2rem;
  }
`;


const ProjectInfo = styled.div`
  flex: 1;
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const ProjectCategory = styled.span`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  color: #00ff00;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ProjectDescription = styled.p`
  color: #cbd5e1;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  flex-grow: 1;
  
  @media (max-width: 768px) {
    line-height: 1.8;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.9;
    margin-bottom: 1rem;
  }
`;

const Technologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    gap: 0.4rem;
    margin-bottom: 1.2rem;
  }
`;

const TechTag = styled.span`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  color: #00ff00;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  
  @media (max-width: 480px) {
    padding: 0.2rem 0.6rem;
    font-size: 0.75rem;
    border-radius: 10px;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
  
  @media (max-width: 480px) {
    gap: 0.8rem;
  }
`;

const ProjectLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #00ff00;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    gap: 0.3rem;
  }
`;

const Projects = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [activeFilter, setActiveFilter] = useState('All');

  const handleFilterClick = (filter) => {
    console.log('Filter clicked:', filter);
    setActiveFilter(filter);
  };

  const projects = [
    {
      id: 1,
      title: 'PrepNest',
      description: 'An offline-first interview preparation platform built with React, TypeScript, and Vite. It features a persistent question vault, spaced-repetition flashcards, an Interview Readiness Index, keyboard-first navigation, responsive Neobrutalist UI, dark/light themes, and local Storage-based data persistence with JSON import/export for seamless offline access.',
      technologies: ['React', 'TypeScript', 'Vite', 'Neobrutalist UI', 'Local Storage'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/PREPNEST',
      live: 'https://prepnestdev.vercel.app/',
      image: '/PrepNest.png'
    },
    {
      id: 2,
      title: 'THREXIA',
      description: 'An enterprise AI-powered security analytics platform designed to detect insider threats through machine learning-based behavioural analysis. It processes enterprise system logs to identify anomalies, generate confidence-based risk assessments, and deliver explainable insights via a secure, role-based dashboard for security operations teams.',
      technologies: ['React', 'Node.js', 'Express.js', 'Machine Learning', 'AI/ML'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/THREXIA',
      live: 'https://threxia.vercel.app/',
      image: '/THREXIA.png'
    },
    {
      id: 3,
      title: 'StudyGenie',
      description: 'An AI-powered educational platform that converts PDF lectures and textbooks into structured study materials using FastAPI, React, and Tailwind CSS. Integrated Gemini, Groq Llama 3.3, and OpenAI LLMs to generate simplified explanations, notes, MCQs, and viva questions. Deployed on Render and Vercel for the Generative AI Hackathon.',
      technologies: ['FastAPI', 'React', 'Tailwind CSS', 'Gemini', 'Groq', 'OpenAI'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/StudyGenie',
      live: 'https://study-genie-gai.vercel.app/',
      image: '/StudyGenie.png'
    },
    {
      id: 4,
      title: 'Laser Eye World',
      description: 'Managed end-to-end SEO operations for the Laser Eye World website, driving improvements through keyword research, on-page optimization, metadata enhancement, and strategic content development. Produced authoritative content on LASIK, cataract, and eye care, contributing to increased organic visibility and user engagement.',
      technologies: ['SEO', 'Keyword Research', 'Content Development', 'Optimization'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69',
      live: 'https://lasereyeworld.com/',
      image: '/LaserEyeWorld.png'
    },
    {
      id: 5,
      title: 'MingleMe',
      description: 'Developed as the capstone project for the MERN Stack Summer Fellowship at Fluxxion. Designed and built a full-featured social media platform from scratch using React, TailwindCSS, Zustand, Node.js, Express.js, and MongoDB Atlas. Implemented secure authentication with email OTP verification, JWT-based sessions, and bcrypt password hashing. Integrated core social features including posts with text/multiple images, hashtags, likes, comments, shares, profile customization, real-time notifications, and advanced search/discovery.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Express.js', 'TailwindCSS', 'Zustand'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/MingleMe',
      live: 'https://mingle-me.vercel.app/',
      image: '/Mingle_Me.png'
    },
    {
      id: 6,
      title: 'Resumly',
      description: 'Developed as the capstone project for the AI Enhanced Web Development Summer Internship at Nexium. Independently designed and built a full-stack platform enabling users to create job-specific resumes using AI (Grog API via n8n workflows) with PDF/DOCX export, rich text editing, dark/light theme support, secure authentication, and responsive design. Tech stack included Next.js, React, TypeScript, Supabase, MongoDB, CkEditor, jsPDF, and Railway.',
      technologies: ['Next.js', 'React', 'TypeScript', 'AI/ML', 'Supabase', 'MongoDB'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/grand-project',
      live: 'https://resumly-tailor.vercel.app/',
      image: '/Resumly.png'
    },
    {
      id: 7,
      title: 'StockHive',
      description: 'Developed a secure, full-stack inventory management platform using Node.js, Express.js, MongoDB, and vanilla JavaScript. Implemented JWT-based authentication with bcrypt password hashing, protected routes, and role-based access. Designed and integrated comprehensive CRUD functionality for products with advanced search, category filtering, and real-time statistics.',
      technologies: ['Node.js', 'Express.js', 'MongoDB', 'JavaScript', 'JWT', 'bcrypt'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/StockHive',
      live: 'https://stock-hive-pm.vercel.app/',
      image: '/StockHive.png'
    },
    {
      id: 8,
      title: 'MediCare',
      description: 'Built a full-stack app with the help of AI to manage 100+ beds, 50+ equipment, and 200+ staff across 10 departments. Developed 15+ RESTful APIs and optimized SQL queries, cutting processing time by 30% and handling 500+ daily transactions with AI-assisted scalability.',
      technologies: ['Node.js', 'Express', 'SQL', 'React.js', 'AI/ML'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/Hospital-Resource-Management',
      live: 'https://medicare-hrms.vercel.app/',
      image: '/Medicare.png'
    },
    {
      id: 9,
      title: 'Bloggily',
      description: 'A Next.js application that scrapes blog content, generates summaries, translates them to Urdu, and stores data in Supabase and MongoDB.',
      technologies: ['Next.js 14', 'React 18', 'TypeScript', 'Supabase (PostgreSQL)', 'MongoDB Atlas'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/Nexium_Anas_Assign2',
      live: 'https://blog-summarizer-web.vercel.app/',
      image: '/Bloggily.png'
    },
    {
      id: 10,
      title: 'NES CRM',
      description: 'A modern, responsive, and visually appealing mini CRM (Customer Relationship Management) system built with React and Tailwind CSS.',
      technologies: ['React', 'Tailwind CSS', 'react-icons'],
      category: 'Full Stack',
      github: 'https://github.com/Anasnaveed69/NES-CRM',
      live: 'https://nes-crm.vercel.app/',
      image: '/NES_CRM.png'
    },
    {
      id: 11,
      title: 'Maze Runner',
      description: 'Programmed a 20x20 procedurally generated maze with WASD controls, 10+ enemies, 15+ collectibles, and 100+ objects at 20 FPS. Implemented live scoring (500+ points) and a Superman Mode toggle.',
      technologies: ['Assembly', 'x86 Architecture', 'Game Development'],
      category: 'Game Development',
      github: 'https://github.com/Anasnaveed69/MAZE-RUNNER',
      live: null,
      image: '/MazeRunner.png'
    },
    {
      id: 12,
      title: 'INFINITY App',
      description: 'Developed a C++ social media app with SFML graphics, supported 10+ profiles, 5+ pages, 20+ posts, with liking, commenting, and sharing features. Designed 8+ OOP classes with arrays and double pointers, real-time timelines, friend lists, and data persistence via text files.',
      technologies: ['C++', 'SFML', 'STL', 'OOP', 'Graphics'],
      category: 'Desktop App',
      github: 'https://github.com/Anasnaveed69/INFINITY-APP',
      live: null,
      image: '/Infinity_App.jpg'
    },
    {
      id: 13,
      title: 'Space Invader Game',
      description: 'Built a retro-style Space Invader using help.h graphics, featured 50 aliens across 2 levels, real-time controls, enemy AI, collision detection, modular scoring (100 points/alien), save/load for 5+ sessions, and top 5 high-score tracking at 30 FPS.',
      technologies: ['C++', 'help.h Graphics Library', 'Game Development'],
      category: 'Game Development',
      github: 'https://github.com/Anasnaveed69/Space-Invader',
      live: null,
      image: '/SpaceInvader.jpg'
    },
    {
      id: 14,
      title: 'Quote Generator',
      description: 'A modern, responsive web application that generates inspiring quotes based on any topic you enter. Built with Next.js 15, React 19, and Tailwind CSS, it features a beautiful UI, dark mode, and a curated database of motivational, leadership, wisdom, and love quotes',
      technologies: ['Next.js 15', 'React 19', 'shadcn/ui', 'Radix UI', 'Tailwind CSS'],
      category: 'Desktop App',
      github: 'https://github.com/Anasnaveed69/Nexium_Anas_Assign1',
      live: 'https://nexium-anas-assign1-quote-generator.vercel.app/',
      image: '/QuoteGenerator.png'
    }
  ];

  const filters = ['All', 'Full Stack', 'Game Development', 'Desktop App'];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  // Debug logging
  console.log('Active Filter:', activeFilter);
  console.log('Filtered Projects Count:', filteredProjects.length);
  console.log('All Projects:', projects.map(p => ({ title: p.title, category: p.category })));

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <ProjectsSection id="projects" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          Featured Projects
        </SectionTitle>

        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Here are some of my recent projects that showcase my skills and experience
        </SectionSubtitle>

        <FilterContainer
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {filters.map((filter, index) => (
            <FilterButton
              key={filter}
              active={activeFilter === filter}
              onClick={() => handleFilterClick(filter)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
            >
              {filter}
            </FilterButton>
          ))}
        </FilterContainer>

        <ProjectsGrid>
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                variants={cardVariants}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                whileHover={{
                  scale: 1.02,
                  rotateY: 5,
                  rotateX: 5
                }}
                layout
              >
                {project.image && (
                  <ProjectImage>
                    <img src={project.image} alt={project.title} />
                  </ProjectImage>
                )}
                <ProjectHeader>

                  <ProjectInfo>
                    <ProjectTitle>{project.title}</ProjectTitle>
                    <ProjectCategory>{project.category}</ProjectCategory>
                  </ProjectInfo>
                </ProjectHeader>

                <ProjectDescription>{project.description}</ProjectDescription>

                <Technologies>
                  {project.technologies.map((tech, techIndex) => (
                    <TechTag key={techIndex}>{tech}</TechTag>
                  ))}
                </Technologies>

                <ProjectLinks>
                  <ProjectLink
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FiGithub />
                    Code
                  </ProjectLink>

                  {project.live && (
                    <ProjectLink
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FiExternalLink />
                      Live Demo
                    </ProjectLink>
                  )}
                </ProjectLinks>
              </ProjectCard>
            ))}
          </AnimatePresence>
        </ProjectsGrid>
      </Container>
    </ProjectsSection>
  );
};

export default Projects;
